import React from 'react';
import { motion } from 'framer-motion';
import { Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Link from '@/components/Link';

export default function BoardMembers() {
  return (
    <section className="py-16 sm:py-20 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-rose-100/30 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute -bottom-32 left-1/2 w-96 h-96 bg-amber-100/30 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-rose-600 font-medium tracking-[0.2em] uppercase text-sm mb-4">Our Leadership</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-gray-900">
            Meet Our Board of Directors
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
            Dedicated leaders committed to transforming communities and creating lasting impact across Ghana and beyond.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Left side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">Experienced & Passionate Leaders</h3>
              <p className="text-gray-600 leading-relaxed">
                Our board comprises healthcare professionals, educators, entrepreneurs, and community advocates who bring diverse expertise and unwavering commitment to our mission.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-rose-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Medical & Healthcare Expertise</p>
                  <p className="text-sm text-gray-600">Leading medical professionals ensure quality health initiatives</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-rose-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Strategic Vision</p>
                  <p className="text-sm text-gray-600">Focused on sustainable, long-term community development</p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-rose-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Global Network</p>
                  <p className="text-sm text-gray-600">Connected with international partners and donors</p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Link to="/team">
                <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-8 inline-flex items-center gap-2">
                  View Full Board
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Right side - Visual element */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-rose-600 to-amber-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
              <Users className="w-16 h-16 mb-6 opacity-20" />
              <h3 className="text-3xl font-bold mb-4">6</h3>
              <p className="text-white/80 text-lg mb-8">Dedicated Board Members</p>
              <div className="space-y-3 text-sm">
                <p className="flex items-start gap-2">
                  <span className="inline-block w-2 h-2 bg-white rounded-full mt-1.5 flex-shrink-0"></span>
                  <span>Diverse expertise and backgrounds</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="inline-block w-2 h-2 bg-white rounded-full mt-1.5 flex-shrink-0"></span>
                  <span>Committed to community impact</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="inline-block w-2 h-2 bg-white rounded-full mt-1.5 flex-shrink-0"></span>
                  <span>Always open to passionate new members</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
